import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FileText, User, DollarSign, Calendar, CheckCircle, Trash2, Eye } from 'lucide-react'
import { db } from '../../firebase'
import { collection, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore'
import ConfirmModal from '../ConfirmModal'

const ApplicationsSection = () => {
  const [applications, setApplications] = useState([])
  const [selectedApp, setSelectedApp] = useState(null)
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, appId: null })

  useEffect(() => {
    const appsRef = collection(db, 'projectApplications')
    
    const unsubscribe = onSnapshot(appsRef, (snapshot) => {
      const loaded = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }))
      loaded.sort((a, b) => b.createdAt - a.createdAt)
      setApplications(loaded)
    })

    return () => unsubscribe()
  }, [])

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'projectApplications', id))
      if (selectedApp?.id === id) {
        setSelectedApp(null)
      }
    } catch (error) {
      console.error('Error deleting application:', error)
    }
  }

  const openDeleteConfirm = (id) => {
    setConfirmModal({ isOpen: true, appId: id })
  }

  const closeDeleteConfirm = () => {
    setConfirmModal({ isOpen: false, appId: null })
  }

  const confirmDelete = () => {
    if (confirmModal.appId) {
      handleDelete(confirmModal.appId)
    }
  }

  const getStatusColor = (budget) => {
    if (budget?.includes('5000+')) return 'text-green-400 bg-green-400/10'
    if (budget?.includes('2000')) return 'text-yellow-400 bg-yellow-400/10'
    return 'text-blue-400 bg-blue-400/10'
  }

  return (
    <div className="h-[calc(100vh-200px)] flex gap-6">
      {/* Applications List */}
      <div className="w-96 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-4 overflow-y-auto">
        <h3 className="text-lg font-display font-bold text-gold-400 mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Aplikacionet ({applications.length})
        </h3>

        <div className="space-y-2">
          {applications.map((app) => (
            <motion.div
              key={app.id}
              onClick={() => setSelectedApp(app)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                selectedApp?.id === app.id
                  ? 'bg-gradient-gold text-white'
                  : 'bg-dark/30 hover:bg-dark/50 text-cream'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-semibold">{app.name}</p>
                  <p className="text-xs opacity-80">{app.company || 'Individual'}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(app.budget)}`}>
                  {app.budget}
                </span>
              </div>
              <p className="text-xs opacity-70 truncate mb-1">{app.projectType}</p>
              <p className="text-xs opacity-50">
                {app.createdAt.toLocaleDateString('sq-AL')}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Application Detail */}
      <div className="flex-1 bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-6 overflow-y-auto">
        {selectedApp ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-display font-bold text-gold-400">
                  {selectedApp.name}
                </h3>
                <p className="text-beige-300">{selectedApp.email}</p>
                <p className="text-beige-400 text-sm">{selectedApp.phone}</p>
              </div>
              <button
                onClick={() => openDeleteConfirm(selectedApp.id)}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-all"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1 flex items-center">
                  <FileText className="w-4 h-4 mr-1" />
                  Tipi i Projektit
                </p>
                <p className="text-cream font-semibold">{selectedApp.projectType}</p>
              </div>
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1 flex items-center">
                  <DollarSign className="w-4 h-4 mr-1" />
                  Buxheti
                </p>
                <p className="text-cream font-semibold">{selectedApp.budget}</p>
              </div>
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Timeline
                </p>
                <p className="text-cream font-semibold">{selectedApp.timeline}</p>
              </div>
              <div className="bg-dark/30 rounded-xl p-4">
                <p className="text-sm text-beige-400 mb-1">Kompania</p>
                <p className="text-cream font-semibold">{selectedApp.company || 'N/A'}</p>
              </div>
            </div>

            <div className="bg-dark/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-beige-300 mb-2">Përshkrimi i Projektit:</h4>
              <p className="text-cream leading-relaxed">{selectedApp.description}</p>
            </div>

            <div className="flex space-x-3">
              <a
                href={`mailto:${selectedApp.email}`}
                className="btn-primary flex-1 justify-center"
              >
                Dërgo Ofertë
              </a>
              <a
                href={`tel:${selectedApp.phone}`}
                className="btn-secondary flex-1 justify-center"
              >
                Telefono
              </a>
            </div>
          </motion.div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-beige-400 mx-auto mb-4 opacity-50" />
              <p className="text-beige-300">Zgjidh një aplikacion për të parë detajet</p>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        title="Fshi Aplikacionin"
        message="Dëshiron ta fshish këtë aplikacion? Kë veprim nuk mund të zhbëhet."
        confirmText="Po, Fshije"
        cancelText="Jo, Anulo"
      />
    </div>
  )
}

export default ApplicationsSection
